import React, { useId } from 'react';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

type IconName = keyof typeof icons;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: IconName;
  rightIcon?: IconName;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      className,
      id,
      'aria-describedby': describedByProp,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    const describedBy = [describedByProp, error && errorId, helperText && helperId]
      .filter(Boolean)
      .join(' ') || undefined;

    const renderIcon = (iconName?: IconName, size = 'h-4 w-4') => {
      if (!iconName) return null;
      const Icon = icons[iconName];
      return Icon ? <Icon className={size} aria-hidden="true" /> : null;
    };

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {label}
            {props.required && (
              <>
                <span className="ml-1 text-emergency-600 dark:text-emergency-400" aria-hidden="true">*</span>
                <span className="sr-only"> erforderlich</span>
              </>
            )}
          </label>
        )}
        <div className="relative">
          {renderIcon(leftIcon, 'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400')}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-primary-400 dark:focus:ring-primary-400/20',
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              error && 'border-emergency-500 focus:border-emergency-500 focus:ring-emergency-500/20 dark:border-emergency-500 dark:focus:border-emergency-400 dark:focus:ring-emergency-400/20',
              className
            )}
            {...props}
            aria-invalid={error ? true : undefined}
            aria-required={props.required || undefined}
            aria-describedby={describedBy}
          />
          {renderIcon(rightIcon, 'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400')}
        </div>
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-emergency-700 dark:text-emergency-400" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="mt-1.5 text-xs leading-4 text-neutral-500 dark:text-neutral-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
